<script lang="ts">
  import Modal from '../Modal.svelte';
  import Upload from '../Upload.svelte';
  import Text from '../Text.svelte';
  import Button from '../Button.svelte';
  import Table from '../Table.svelte';
  import { readFileAsText } from '../../utils/read-file-as-text.js';
  import { RedirectService, type CreateRedirect } from '../../utils/redirect.service.js';
  import Checkbox from '../Checkbox.svelte';
  import { addToast } from '../toast/store.js';
  import LoadingSpinner from '../LoadingSpinner.svelte';
  import { getUserErrorMessage, ServiceError } from 'src/utils/error.js';

  type ImportError = { redirect: CreateRedirect; error: { code: string } };

  export let isOpen: boolean;
  export let onImportSuccess: (importedRedirectsCount: string) => void;

  const redirectService = new RedirectService();
  const maxRowsCount = 1500;
  const columns = [
    { label: 'Quelle', sortable: false, field: 'source' },
    { label: 'Ziel', sortable: false, field: 'destination' },
    { label: 'Typ', sortable: false, field: 'redirect_type' },
    { label: 'Tag', sortable: false, field: 'tag' }
  ];

  let files: FileList | undefined;
  let rows: CreateRedirect[] = [];
  let importErrors: ImportError[] = [];
  let overrideExistingEntries: boolean = false;
  let loading = false;

  const importRedirects = async () => {
    loading = true;
    try {
      const response = await redirectService.bulkInsert(rows, overrideExistingEntries);
      onImportSuccess(response.responseData.count);
    } catch (error: unknown) {
      if (error instanceof ServiceError && error.code === 406) {
        importErrors = JSON.parse(error.message);
      } else {
        const message = getUserErrorMessage(
          error,
          'Unerwarteter Fehler beim Importieren der Redirects.'
        );
        addToast({ type: 'error', message });
      }
    } finally {
      loading = false;
    }
  };

  const parseCSVFile = async () => {
    importErrors = [];
    if (files && files.length) {
      const csvText = await readFileAsText(files[0]);

      if (csvText && typeof csvText === 'string') {
        rows = csvText
          .split('\n')
          .filter(row => !!row.trim())
          .map((row: string) => {
            const rowColumns = row.split(';');
            return {
              source: rowColumns[0],
              destination: rowColumns[1],
              redirect_type: rowColumns[2],
              tag: rowColumns[3].toLocaleLowerCase().trim()
            };
          });
      }
    }
  };

  const resetForm = () => {
    files = undefined;
    rows = [];
    importErrors = [];
    overrideExistingEntries = false;
  };
</script>

<Modal size="large" label="Weiterleitungen im Bulk hinzufügen" bind:isOpen onClose={resetForm}>
  {#if loading}
    <LoadingSpinner />
  {/if}
  <svelte:fragment slot="fixed">
    <Text size="medium"
      >Laden Sie die CSV-Datei mit den gewünschten Weiterleitungen hoch. Es sind maximal 1.500
      Zeilen pro Import möglich, URLs müssen encodiert sein.</Text
    >

    <Upload
      name="bulk-redirects"
      accept=".csv"
      label="Datei hochladen"
      bind:files
      on:change={parseCSVFile}
    />

    {#if importErrors.length}
      <div class="error-table-container">
        <Text size="normal" color="highlightRed" className="error-table-headline"
          >Es gab {importErrors.length} Fehler beim Hochladen der folgenden Weiterleitungen:
        </Text>

        <ul class="error-legend">
          <li>
            <span class="error-legend-code">20</span>Weiterleitung mit der gleichen Quelle existiert
            bereits
          </li>
          <li><span class="error-legend-code">30</span>Weiterleitungsdaten sind ungültig</li>
        </ul>

        <Table
          rows={importErrors.map(importError => ({
            ...importError.redirect,
            error: importError.error.code
          }))}
          columns={[{ label: 'Fehler', sortable: false, field: 'error' }, ...columns]}
        />
      </div>
    {/if}
  </svelte:fragment>

  {#if rows.length}
    {#if rows.length > maxRowsCount}
      <Text size="medium" color="highlightRed"
        >Maximale Anzahl von {maxRowsCount} Zeilen überschritten.</Text
      >
    {:else}
      <div class="data-table">
        <Table {rows} {columns} />
      </div>
    {/if}
  {/if}

  <svelte:fragment slot="button">
    <Button on:click={() => (isOpen = false)}>Abbrechen</Button>
    <Button
      disabled={!rows.length || rows.length > maxRowsCount}
      on:click={() => {
        importRedirects();
      }}
      variant="primary">Importieren</Button
    >
    <div class="checkbox-wrapper">
      <Checkbox
        label="Weiterleitungen mit gleicher Quelle überschreiben?"
        bind:checked={overrideExistingEntries}
      />
    </div>
  </svelte:fragment>
</Modal>

<style lang="scss">
  @use '../../styles/colors';
  @use '../../styles/spacing';
  @use '../../styles/font-sizes';

  .checkbox-wrapper {
    display: flex;
    width: 100%;
    justify-content: end;
    margin-top: spacing.$m;
  }

  // override table styles
  .data-table {
    :global(th) {
      position: sticky;
      top: 0;
      background-color: #f5f5f5;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      min-width: 40px;
    }

    :global(td) {
      overflow-wrap: anywhere;
    }
  }

  .error-table-container {
    margin-top: spacing.$m;

    :global(.table-wrapper) {
      max-height: 500px;
      overflow-y: scroll;
    }

    :global(td) {
      overflow-wrap: anywhere;
      min-width: 40px;
      &:first-child {
        width: 10%;
      }
    }

    :global(.error-table-headline) {
      margin-bottom: spacing.$xs;
    }
  }
  .error-legend {
    font-size: font-sizes.$text-s;
    margin: spacing.$s 0 spacing.$xs;

    li {
      margin: spacing.$xxs 0;
    }
  }

  .error-legend-code {
    border: 1px solid black;
    padding: spacing.$xxxs;
    margin-right: spacing.$xxs;
    display: inline-block;
    width: 22px;
    text-align: center;
  }
</style>
